import { ErrorSummary, Link } from '@atoms/Link/Link';
import { FormTitle } from '@molecules/FormTitle/FormTitle';
import {
  NavigationButtonProps,
  NavigationButtons,
} from '@molecules/NavigationButtons/NavigationButtons';
import { TextInputMolecule } from '@molecules/TextInput/TextInput';
import { ErrorMessages } from '@types';
import { ChangeEvent } from 'react';

export type UserData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
};

export interface YourDetailsProps {
  isLoadingUserData: boolean;
  isLoggedInUser: boolean | undefined;
  userData?: UserData | null;
  buttonNext: NavigationButtonProps;
  buttonPrevious: NavigationButtonProps;
  errorMessages: ErrorMessages[];
  handleLogin: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  idProvider?: string; // idProvider is undefined for WCC Dynamics account, 'google.com' for Google account, 'facebook.com' for Facebook account
}

export const YourDetailsTemplate = (props: YourDetailsProps) => {
  const dataProtectionLink = (
    <Link
      linkType="inline"
      external={true}
      href={
        'https://www.westminster.gov.uk/data-protection/fair-processing-notice'
      }
    >
      Westminster City Council&apos;s data protection notice.
    </Link>
  );

  const isDataFromWestminsterAccount =
    props.isLoggedInUser && !props.idProvider;

  return (
    <div className="body-1">
      <ErrorSummary messages={props.errorMessages} />
      {props.isLoadingUserData ? (
        <>Loading user data...</>
      ) : (
        <>
          <div className="mb-6">
            <FormTitle text="Your details" />
            {isDataFromWestminsterAccount ? (
              <p className="text-bodyLg text-text-body mb-1">
                Your details are taken from your MyWestminster account and
                cannot be edited here.
              </p>
            ) : (
              <p className="text-bodyLg text-text-body mb-1">
                You can also log-into or sign-up for a{' '}
                <Link
                  linkType="inline"
                  className="text-action-link hover:text-action-link-hover cursor-pointer underline"
                  onClick={props.handleLogin}
                >
                  MyWestminster account
                </Link>{' '}
                to receive a confirmation email.
              </p>
            )}
          </div>

          <form className="flex flex-col gap-y-2 pb-20">
            <div>
              {/* disable text input if user is logged in and data input comes from WCC Dynamics */}
              <TextInputMolecule
                label="First name"
                name="firstName"
                itemID="firstName"
                disabled={isDataFromWestminsterAccount}
                value={props.userData?.firstName}
                type="text"
                onChange={props.onChange}
                errorPath="firstName"
                errorMessages={props.errorMessages}
              />
            </div>
            <div>
              {/* disable text input if user is logged in and data input comes from WCC Dynamics */}
              <TextInputMolecule
                label="Last name"
                name="lastName"
                itemID="lastName"
                disabled={isDataFromWestminsterAccount}
                value={props.userData?.lastName}
                type="text"
                onChange={props.onChange}
                errorPath="lastName"
                errorMessages={props.errorMessages}
              />
            </div>

            <div>
              {/* disable text input if user is logged in; email data may come from WCC Dynamics, Google or Facebook account */}
              <TextInputMolecule
                label="Email"
                name="email"
                itemID="email"
                disabled={props.isLoggedInUser}
                value={props.userData?.email}
                type="text"
                onChange={props.onChange}
                errorPath="email"
                errorMessages={props.errorMessages}
              />
            </div>

            {props.userData && 'phoneNumber' in props.userData && (
              <div>
                {/* disable text input if user is logged in; email data may come from WCC Dynamics, Google or Facebook account */}
                <TextInputMolecule
                  label="Contact Number"
                  name="phoneNumber"
                  itemID="phoneNumber"
                  value={props.userData?.phoneNumber}
                  type="text"
                  onChange={props.onChange}
                  errorPath="phoneNumber"
                  errorMessages={props.errorMessages}
                />
              </div>
            )}

            <div className="text-body text-text-body my-4 font-semibold">
              If you would like to know more about how the council processes
              your data, please see {dataProtectionLink}
            </div>

            <NavigationButtons
              buttonNext={props.buttonNext}
              buttonPrevious={props.buttonPrevious}
            />
          </form>
        </>
      )}
    </div>
  );
};
